/**
 * Strategy chat index (ARCHITECTURE.md §8): the entry to the AI strategy builder.
 *
 * Left of the builder proper, this is the launcher — start a fresh session or a
 * "refine an existing strategy" session (which seeds the right-panel document
 * with that strategy's DSL and gives the model its real performance history), and
 * resume any past session. Gated by the `ai` feature flag: with no provider key,
 * the page becomes a CTA to Settings → AI rather than a dead composer (§8).
 */
import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Badge, formatDateTime } from "@richrich/ui";
import type { ChatSession } from "@richrich/contracts";
import { useI18n } from "@richrich/i18n/react";
import { PageShell } from "../../components/PageShell";
import { AsyncState } from "../../components/ui/AsyncState";
import { ConfirmButton, useConfirmable } from "../../components/ui/ConfirmButton";
import { FormError } from "../../components/ui/FormError";
import { settingsQuery } from "../../lib/query";
import { chatSessionsQuery, strategiesListQuery } from "../../features/chat/api";
import { useCreateChatSession, useDeleteChatSession } from "../../features/chat/hooks";

export const Route = createFileRoute("/chat/")({
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(chatSessionsQuery),
      context.queryClient.ensureQueryData(settingsQuery),
    ]);
  },
  component: ChatIndexPage,
});

function ChatIndexPage() {
  const { t } = useI18n();
  const settings = useQuery(settingsQuery);
  const sessions = useQuery(chatSessionsQuery);
  const aiUnlocked = settings.data?.features.ai ?? false;

  return (
    <PageShell
      eyebrow={t("chat.eyebrow")}
      title={t("chat.pageTitle")}
      description={t("chat.pageDescription")}
    >
      <div className="flex flex-col gap-6">
        {aiUnlocked ? <NewSession /> : <LockedNotice />}

        <section className="flex flex-col gap-3">
          <h2 className="font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-muted">
            {t("chat.sessionsHeading")}
          </h2>
          <AsyncState
            query={sessions}
            skeleton={<ListSkeleton />}
            error={
              <p className="py-8 text-center text-sm text-critical">
                {t("chat.sessionsLoadError")}
              </p>
            }
            empty={<EmptySessions />}
            isEmpty={(data) => data.sessions.length === 0}
          >
            {(data) => (
              <ul className="flex flex-col gap-2">
                {data.sessions.map((s) => (
                  <SessionRow key={s.id} session={s} />
                ))}
              </ul>
            )}
          </AsyncState>
        </section>
      </div>
    </PageShell>
  );
}

// ── new session ─────────────────────────────────────────────────
function NewSession() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const strategies = useQuery(strategiesListQuery);
  const [title, setTitle] = useState("");
  const [strategyId, setStrategyId] = useState("");

  const start = useCreateChatSession();

  const startSession = (): void => {
    start.mutate(
      {
        ...(title.trim() === "" ? {} : { title: title.trim() }),
        ...(strategyId === "" ? {} : { strategyId }),
      },
      {
        onSuccess: async (session) => {
          await navigate({ to: "/chat/$sessionId", params: { sessionId: session.id } });
        },
      },
    );
  };

  const refinable = (strategies.data?.strategies ?? []).filter((s) => s.status !== "archived");

  return (
    <section className="rounded-xl border border-hairline bg-surface p-5">
      <h2 className="text-sm font-semibold text-ink">{t("chat.startSessionHeading")}</h2>
      <p className="mt-0.5 text-xs text-muted">{t("chat.startSessionDesc")}</p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted">
            {t("chat.titleLabel")}
          </span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("chat.titlePlaceholder")}
            className="rounded-lg border border-hairline bg-page px-3 py-2 text-sm text-ink placeholder:text-muted focus:border-brand/40 focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted">
            {t("chat.baseLabel")}
          </span>
          <select
            value={strategyId}
            onChange={(e) => setStrategyId(e.target.value)}
            className="rounded-lg border border-hairline bg-page px-3 py-2 text-sm text-ink focus:border-brand/40 focus:outline-none"
          >
            <option value="">{t("chat.startFresh")}</option>
            {refinable.map((s) => (
              <option key={s.id} value={s.id}>
                {t("chat.refineOption", { name: s.name })}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          disabled={start.isPending}
          onClick={startSession}
          className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand/90 disabled:opacity-60"
        >
          {start.isPending ? t("chat.starting") : t("chat.startBuilding")}
        </button>
        <FormError error={start.error} />
      </div>
    </section>
  );
}

function LockedNotice() {
  const { t } = useI18n();
  return (
    <section className="rounded-xl border border-dashed border-hairline bg-surface p-6 text-center">
      <h2 className="text-sm font-semibold text-ink">{t("chat.aiNotConfigured")}</h2>
      <p className="mx-auto mt-1 max-w-md text-xs text-muted">{t("chat.aiLockedDesc")}</p>
      <Link
        to="/settings/ai"
        className="mt-4 inline-flex rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand/90"
      >
        {t("chat.configureAi")}
      </Link>
    </section>
  );
}

// ── session list ────────────────────────────────────────────────
function SessionRow({ session }: { session: ChatSession }) {
  const { t, locale } = useI18n();
  const confirmable = useConfirmable();

  const remove = useDeleteChatSession();

  return (
    <li onMouseLeave={confirmable.reset}>
      <div className="flex items-center gap-2 rounded-lg border border-hairline bg-surface px-4 py-3 transition-colors hover:border-brand/40">
        <Link
          to="/chat/$sessionId"
          params={{ sessionId: session.id }}
          className="flex min-w-0 flex-1 items-center justify-between gap-3"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium text-ink">
                {session.title ?? t("chat.untitledSession")}
              </span>
              {session.strategyId !== null && <Badge tone="info">{t("chat.refining")}</Badge>}
              {session.draftDsl !== null && <Badge tone="neutral">{t("chat.hasDraft")}</Badge>}
            </div>
            <div className="mt-0.5 text-xs text-muted">
              {t("chat.updated", { date: formatDateTime(session.updatedAt, locale) })}
            </div>
          </div>
          <span className="shrink-0 text-muted" aria-hidden="true">
            →
          </span>
        </Link>
        <ConfirmButton
          confirmable={confirmable}
          pending={remove.isPending}
          onConfirm={() => remove.mutate(session.id)}
          ariaLabel={t("chat.deleteSession")}
          idle={t("chat.deleteSession")}
          confirm={t("chat.deleteConfirm")}
          busy={t("chat.deleting")}
        />
      </div>
      {remove.isError && <p className="mt-1 px-1 text-xs text-critical">{t("chat.deleteError")}</p>}
    </li>
  );
}

function EmptySessions() {
  const { t } = useI18n();
  return (
    <div className="rounded-xl border border-dashed border-hairline py-12 text-center">
      <p className="text-sm font-medium text-ink">{t("chat.sessionsEmptyTitle")}</p>
      <p className="mt-1 text-sm text-muted">{t("chat.sessionsEmptyDesc")}</p>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-[58px] animate-pulse rounded-lg border border-hairline bg-surface"
        />
      ))}
    </div>
  );
}
